import axios from './Axios';
import { PurchaseHistory, Payment, TossReceipt, XenditIntent, CreateXenditIntentParams } from 'ai-api/types/purchase';

/**
 * Service for handling product purchases
 */
export const PurchaseService = {
    /**
     * Fetches product information by ID
     * @param productId Product ID to fetch
     * @param platform Platform type (ios, android, web)
     */
    getProduct: async (productId: string, platform: 'ios' | 'android' | 'web' = 'web') => {
        try {
            const response = await axios.get('/purchase/product', {
                params: {
                    productid: productId,
                    platform
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching product:', error);
            throw error;
        }
    },

    /**
     * Lists all available products
     * @param platform Platform type (ios, android, web)
     * @param type Optional product type filter
     */
    listProducts: async (platform: 'ios' | 'android' | 'web' = 'web', type?: string) => {
        try {
            const response = await axios.get('/purchase/product/list', {
                params: {
                    platform,
                    type
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error listing products:', error);
            throw error;
        }
    },

    /**
     * Creates a Stripe payment intent
     * @param productId Product ID to purchase
     * @param email User's email for receipt (optional)
     * @param name User's name (optional)
     * @param quantity Quantity to purchase (optional, defaults to 1)
     */
    createStripeIntent: async (productId: string, email?: string, name?: string, quantity?: number) => {
        try {
            const response = await axios.put<{code: 200, data: {amount: number, clientSecret: string, paymentIntentId: string, quantity: number}}>('/purchase/stripe-intent', {
                productid: productId,
                email,
                name,
                quantity: quantity || 1
            });
            return response.data;
        } catch (error) {
            console.error('Error creating payment intent:', error);
            throw error;
        }
    },

    /**
     * Creates a Toss payment intent
     * @param productId Product ID to purchase
     * @param successUrl URL to redirect after successful payment
     * @param failUrl URL to redirect after failed payment
     * @param quantity Quantity to purchase (optional, defaults to 1)
     */
    createTossIntent: async (productId: string, successUrl: string, failUrl: string, quantity?: number) => {
        try {
            const response = await axios.put<{code: 200, data: {
                clientKey: string,
                orderid: string,
                orderName: string,
                amount: number,
                quantity: number,
                currency: string,
                customerName: string,
                successUrl: string,
                failUrl: string,
                isTestMode: boolean
            }}>('/purchase/toss-intent', {
                productid: productId,
                successUrl,
                failUrl,
                quantity: quantity || 1
            });
            return response.data;
        } catch (error) {
            console.error('Error creating Toss payment intent:', error);
            throw error;
        }
    },

    /**
     * Creates a Xendit payment intent for Indonesian payment methods
     * @param params Parameters for creating Xendit payment intent
     */
    createXenditIntent: async (params: CreateXenditIntentParams) => {
        try {
            const response = await axios.put<{code: 200, data: XenditIntent}>('/purchase/xendit-intent', params);
            return response.data;
        } catch (error) {
            console.error('Error creating Xendit payment intent:', error);
            throw error;
        }
    },

    /**
     * Gets purchase history for the current user
     * @param status Optional status filter
     * @param type Optional type filter ('subscription' or 'coin')
     * @param limit Optional limit of items to return
     * @param offset Optional offset for pagination
     */
    getPurchaseHistory: async (type?: string, limit: number = 20, offset: number = 0) => {
        try {
            const response = await axios.get<{code: number, data: PurchaseHistory[]}>('/purchase/history', {
                params: {
                    type,
                    limit,
                    after: offset
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching purchase history:', error);
            throw error;
        }
    },

    /**
     * Gets details of a specific purchase
     * @param purchaseId ID of the purchase to fetch
     */
    getPurchaseDetails: async (purchaseId: string) => {
        try {
            const response = await axios.get<{code: number, data: PurchaseHistory}>(`/purchase/${purchaseId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching purchase ${purchaseId}:`, error);
            throw error;
        }
    },

    /**
     * Cancels a purchase (if eligible)
     * @param purchaseId ID of the purchase to cancel
     * @param reason Optional reason for cancellation
     */
    cancelPurchase: async (purchaseId: string, reason?: string) => {
        try {
            const response = await axios.post(`/purchase/${purchaseId}/cancel`, { reason });
            return response.data;
        } catch (error) {
            console.error(`Error cancelling purchase ${purchaseId}:`, error);
            throw error;
        }
    },

    /**
     * Reschedules a session for a purchase
     * @param purchaseId ID of the purchase to reschedule
     * @param newDate New session date (ISO string)
     * @param newTime New session time (ISO string)
     */
    reschedulePurchase: async (purchaseId: string, newDate: string, newTime: string) => {
        try {
            const response = await axios.post(`/purchase/${purchaseId}/reschedule`, {
                date: newDate,
                time: newTime
            });
            return response.data;
        } catch (error) {
            console.error(`Error rescheduling purchase ${purchaseId}:`, error);
            throw error;
        }
    },

    /**
     * Validates Toss payment
     * @param orderid Order ID
     * @param productid Product ID
     * @param receipt Toss receipt data
     */
    validateTossPayment: async (orderid: string, productid: string | null, receipt: TossReceipt) => {
        try {
            const response = await axios.put<{code: number, data: {valid: boolean}}>('/purchase/payment', {
                platform: 'toss',
                orderid,
                productid,
                receipt
            });
            return response.data;
        } catch (error) {
            console.error('Error validating Toss payment:', error);
            throw error;
        }
    },

    /**
     * Validates Xendit payment
     * @param orderid Order ID
     * @param productid Product ID
     * @param paymentRequestId Xendit payment request ID
     * @param amount Payment amount to verify
     */
    validateXenditPayment: async (orderid: string, productid: string | null, paymentRequestId: string, amount: number) => {
        try {
            const response = await axios.put<{code: number, data: {valid: boolean}}>('/purchase/payment', {
                platform: 'xendit',
                orderid,
                productid,
                receipt: {
                    paymentRequestId,
                    orderId: orderid,
                    amount
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error validating Xendit payment:', error);
            throw error;
        }
    },

    /**
     * Gets payment information
     * @param orderid Order ID to fetch
     */
    getPayment: async (orderid: string) => {
        try {
            const response = await axios.get<{code: number, data: Payment[]}>('/purchase/payment', {
                params: { orderid }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching payment info:', error);
            throw error;
        }
    }
};

export default PurchaseService;